import { ChevronLeft, ChevronRight, CircleHelp } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import { Input, Select } from '../../../components/ui/FormField.js';
import { STATUS_FILTERS, type StatusFilter } from '../scheduleParams.js';
import type { ScheduleView } from '../timeline/dropLogic.js';

const VIEWS: Array<{ view: ScheduleView; label: string }> = [
  { view: 'day', label: 'Day' },
  { view: 'week', label: 'Week' },
  { view: 'month', label: 'Month' },
];

export interface ScheduleToolbarProps {
  view: ScheduleView;
  /** The date the platform is showing, or '' before the first answer. */
  date: string;
  disabled: boolean;
  plate: string;
  status: StatusFilter | '';
  location: string;
  locations: Array<{ id: string; name: string }>;
  unavailable: boolean;
  onView: (view: ScheduleView) => void;
  onStep: (direction: 1 | -1) => void;
  onToday: () => void;
  onDate: (date: string) => void;
  onPlate: (plate: string) => void;
  onStatus: (status: StatusFilter | '') => void;
  onLocation: (location: string) => void;
  onUnavailable: (on: boolean) => void;
  /** 329: the Free for control, shown with the other filters. */
  freeFor?: ReactNode;
  /** 329: the Display menu (Group by, density, Reorder cars), at the right. */
  displayMenu?: ReactNode;
  /** 329: the Guide button, shown when `onGuide` is given. */
  guideOpen?: boolean;
  onGuide?: () => void;
  guideButtonRef?: Ref<HTMLButtonElement>;
}

/**
 * 316 (US6, FR-020) — one compact toolbar. It fits one row at desktop width and wraps into rows on a
 * narrow screen, never into page-level horizontal scroll; labels never wrap (`whitespace-nowrap`).
 *
 * 329 (FR-001): three groups — navigate (view, previous / Today / next, date), and at the right the
 * Display menu, then the Guide button; the filters (plate, status, location, Free for, unavailable
 * cars) take the second row. At 1440 the three need 1,386 px of 1,055 (Gate A), so the product owner
 * chose two tidy rows over hiding controls. The markup follows the reading order, so Tab goes where
 * the eye does. The line that used to sit under the toolbar is gone.
 */
export function ScheduleToolbar(p: ScheduleToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Schedule controls"
      data-testid="schedule-day-nav"
      className="flex flex-wrap items-center gap-x-5 gap-y-2"
    >
      <fieldset
        aria-label="Navigate"
        data-testid="schedule-toolbar-navigate"
        className="flex flex-wrap items-center gap-2"
      >
        <div
          role="radiogroup"
          aria-label="View"
          className="flex shrink-0 rounded border border-hairline"
        >
          {VIEWS.map((v) => (
            <button
              key={v.view}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: #950 a segmented control: buttons with role="radio" in a radiogroup is the ARIA pattern; native radio inputs would change the look and focus behaviour
              role="radio"
              aria-checked={p.view === v.view}
              onClick={() => p.view !== v.view && p.onView(v.view)}
              className={[
                'whitespace-nowrap px-3 py-1.5 text-sm first:rounded-l last:rounded-r',
                p.view === v.view ? 'bg-primary text-on-primary' : 'bg-canvas text-ink',
              ].join(' ')}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            aria-label={`Previous ${p.view}`}
            disabled={p.disabled}
            onClick={() => p.onStep(-1)}
            className="rounded border border-hairline p-1.5 text-ink disabled:opacity-50"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={p.onToday}
            className="whitespace-nowrap rounded border border-hairline px-3 py-1 text-sm text-ink"
          >
            Today
          </button>
          <button
            type="button"
            aria-label={`Next ${p.view}`}
            disabled={p.disabled}
            onClick={() => p.onStep(1)}
            className="rounded border border-hairline p-1.5 text-ink disabled:opacity-50"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
          <input
            type="date"
            aria-label="Day"
            value={p.date}
            onChange={(e) => e.target.value && p.onDate(e.target.value)}
            className="rounded border border-hairline bg-canvas px-2 py-1 text-sm text-ink"
          />
        </div>
      </fieldset>

      <fieldset
        aria-label="Display and help"
        data-testid="schedule-toolbar-settings"
        className="ml-auto flex items-center gap-2"
      >
        {p.displayMenu}
        {p.onGuide && (
          <button
            ref={p.guideButtonRef}
            type="button"
            data-testid="schedule-guide-button"
            aria-expanded={Boolean(p.guideOpen)}
            onClick={p.onGuide}
            className={[
              'flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-sm font-semibold',
              p.guideOpen
                ? 'border-brand-blue bg-brand-blue text-on-primary'
                : 'border-hairline-strong bg-canvas text-ink',
            ].join(' ')}
          >
            <CircleHelp aria-hidden="true" size={14} />
            Guide
          </button>
        )}
      </fieldset>

      <fieldset
        aria-label="Filter"
        data-testid="schedule-toolbar-filter"
        className="flex basis-full flex-wrap items-center gap-2"
      >
        {/* The shared field styles are `w-full`, so each field's width comes from a wrapper (a width
          class on the field itself loses to `w-full`, seen at 1440 px in T026). */}
        <div className="w-28 shrink-0">
          <Input
            type="search"
            data-testid="plate-filter"
            aria-label="Filter by plate number"
            placeholder="Plate…"
            className="py-1!"
            value={p.plate}
            onChange={(e) => p.onPlate((e.target as HTMLInputElement).value)}
          />
        </div>
        <div className="w-36 shrink-0">
          <Select
            aria-label="Booking status"
            value={p.status}
            className="py-1!"
            onChange={(e) => p.onStatus((e.target as HTMLSelectElement).value as StatusFilter | '')}
          >
            <option value="">All statuses</option>
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-36 shrink-0">
          <Select
            aria-label="Location"
            value={p.location}
            className="py-1!"
            onChange={(e) => p.onLocation((e.target as HTMLSelectElement).value)}
          >
            <option value="">All locations</option>
            {p.locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </div>
        {p.freeFor}
        {/* 301 (#437) US1: defaults off — a car unavailable for a reason held on itself stays hidden
          until an operator asks to see it. */}
        <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-steel">
          <input
            type="checkbox"
            data-testid="show-unavailable-toggle"
            aria-label="Show unavailable cars"
            checked={p.unavailable}
            onChange={(e) => p.onUnavailable(e.target.checked)}
            className="h-4 w-4 rounded border-hairline"
          />
          <span>Unavailable cars</span>
        </label>
      </fieldset>
    </div>
  );
}
